import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getActivity, createActivity, updateActivity, uploadActivityImage,
  getSessionsForActivity, createSession, updateSession, deleteSession,
} from '../../util/api';
import { resolveId } from '../../util/shortUuid';

// ─── Async Thunks ───

const fetchActivityThunk = createAsyncThunk(
  'EditActivityPage/fetchActivity',
  async (activityId: string, { rejectWithValue }) => {
    try {
      const r = await getActivity(activityId);
      return r.data || r;
    } catch (e: any) { return rejectWithValue(e.message || 'Failed to load activity'); }
  }
);

const fetchSessionsThunk = createAsyncThunk(
  'EditActivityPage/fetchSessions',
  async (activityId: string, { rejectWithValue }) => {
    try {
      const r = await getSessionsForActivity(activityId);
      return r.data || [];
    } catch (e: any) { return rejectWithValue(e.message || 'Failed to load sessions'); }
  }
);

const saveActivityThunk = createAsyncThunk(
  'EditActivityPage/saveActivity',
  async ({ body, isNew }: { body: any; isNew: boolean }, { rejectWithValue }) => {
    try {
      const r = isNew ? await createActivity(body) : await updateActivity(body);
      return r.data || r;
    } catch (e: any) { return rejectWithValue(e.message || 'Failed to save activity'); }
  }
);

const uploadImageThunk = createAsyncThunk(
  'EditActivityPage/uploadImage',
  async ({ activityId, file }: { activityId: string; file: File }, { rejectWithValue }: any) => {
    try {
      const resp = await uploadActivityImage(activityId, file);
      return { imageId: resp.imageId, imageUrl: resp.imageUrl };
    } catch (e: any) { return rejectWithValue(e.message || 'Failed to upload image'); }
  }
);

// ─── Slice ───

const slice = createSlice({
  name: 'EditActivityPage',
  initialState: {
    activity: null as any,
    activityLoading: false,
    activityError: null as string | null,
    sessions: [] as any[],
    sessionsLoading: false,
    sessionsError: null as string | null,
    mutationInProgress: false,
    mutationError: null as string | null,
    imageUploadInProgress: false,
    imageUploadError: null as string | null,
  },
  reducers: {
    clearState: () => slice.getInitialState(),
  },
  extraReducers: builder => {
    builder
      .addCase(fetchActivityThunk.pending, s => { s.activityLoading = true; s.activityError = null; })
      .addCase(fetchActivityThunk.fulfilled, (s, a) => { s.activityLoading = false; s.activity = a.payload; })
      .addCase(fetchActivityThunk.rejected, (s, a) => { s.activityLoading = false; s.activityError = a.payload as string; })

      .addCase(fetchSessionsThunk.pending, s => { s.sessionsLoading = true; s.sessionsError = null; })
      .addCase(fetchSessionsThunk.fulfilled, (s, a) => { s.sessionsLoading = false; s.sessions = a.payload; })
      .addCase(fetchSessionsThunk.rejected, (s, a) => { s.sessionsLoading = false; s.sessionsError = a.payload as string; })

      .addCase(saveActivityThunk.pending, s => { s.mutationInProgress = true; s.mutationError = null; })
      .addCase(saveActivityThunk.fulfilled, (s, a) => { s.mutationInProgress = false; s.activity = a.payload; })
      .addCase(saveActivityThunk.rejected, (s, a) => { s.mutationInProgress = false; s.mutationError = a.payload as string; })

      .addCase(uploadImageThunk.pending, s => { s.imageUploadInProgress = true; s.imageUploadError = null; })
      .addCase(uploadImageThunk.fulfilled, s => { s.imageUploadInProgress = false; })
      .addCase(uploadImageThunk.rejected, (s, a) => { s.imageUploadInProgress = false; s.imageUploadError = a.payload as string; });
  },
});

export default slice.reducer;

// ─── Exported Thunks ───

export const fetchActivity = (id: string) => (dispatch: any) =>
  dispatch(fetchActivityThunk(id)).unwrap();

export const fetchSessions = (id: string) => (dispatch: any) =>
  dispatch(fetchSessionsThunk(id)).unwrap().catch(() => {});

export const saveActivity = (body: any, isNew: boolean) => (dispatch: any) =>
  dispatch(saveActivityThunk({ body, isNew })).unwrap();

export const setActivityStatus = (id: string, status: string) => (dispatch: any) =>
  dispatch(saveActivityThunk({ body: { id, status }, isNew: false })).unwrap();

export const uploadImage = (activityId: string, file: File) => (dispatch: any) =>
  dispatch(uploadImageThunk({ activityId, file })).unwrap();

export const saveSession = (body: any, activityId: string, isNew: boolean) => async (dispatch: any) => {
  if (isNew) await createSession({ ...body, activityId });
  else await updateSession(body);
  dispatch(fetchSessionsThunk(activityId));
};

export const removeSession = (sessionId: string, activityId: string) => async (dispatch: any) => {
  await deleteSession({ id: sessionId });
  dispatch(fetchSessionsThunk(activityId));
};

export const clearEditActivityState = () => slice.actions.clearState();

// ─── loadData ───

export const loadData = (params: any) => (dispatch: any) => {
  const rawId = params?.id;
  if (rawId && rawId !== 'new') {
    const id = resolveId(rawId);
    return Promise.all([dispatch(fetchActivityThunk(id)), dispatch(fetchSessionsThunk(id))]);
  }
  return Promise.resolve();
};

// ─── Selectors ───

export const selectActivity = (s: any) => s.EditActivityPage?.activity || null;
export const selectActivityLoading = (s: any) => s.EditActivityPage?.activityLoading || false;
export const selectSessions = (s: any) => s.EditActivityPage?.sessions || [];
export const selectSessionsLoading = (s: any) => s.EditActivityPage?.sessionsLoading || false;
export const selectMutationInProgress = (s: any) => s.EditActivityPage?.mutationInProgress || false;
export const selectImageUploadInProgress = (s: any) => s.EditActivityPage?.imageUploadInProgress || false;
